/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { setLocation } from 'js/lib/coursera.redirect';

import type { ButtonProps } from '@coursera/cds-core';
import { Button, Grid, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import { ChevronPreviousIcon, CloseIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { HACK_SURVEY_LENGTH } from 'bundles/onboarding-2022/data/constants';
import { getCurrentSection } from 'bundles/onboarding-2022/data/onboardingPageUtils';
import CourseraLogo from 'bundles/page/components/CourseraLogo';

import _t from 'i18n!nls/onboarding-2022';

const TrackedButton = withSingleTracked({ type: 'BUTTON' })<ButtonProps>(Button);

type Props = {
  sectionFlow: string[];
  isMobile: boolean;
  switchPage: () => void;
  isLoadingPage?: boolean;
  isCourseraPlusUpsellPage?: boolean;
  onExitButtonClick?: () => void;
  isRedirecting?: boolean;
};

const styles = {
  headerContainer: css`
    position: relative;
    min-height: 60px;
    height: 80px;
    min-width: 230px;
    padding: var(--cds-spacing-200) var(--cds-spacing-200);
    margin: auto;
    background: #fff;
    filter: drop-shadow(0 2px 3px rgb(0 0 0 / 25%));
    ${breakpoints.down('xs')} {
      height: 66px;
    }
  `,
  courseraLogo: css`
    position: absolute;
    margin: auto;
    top: 50%;
    transform: translateY(-50%) translateX(-50%);
    left: 50%;

    img {
      width: 127px;
      height: 32px;
    }
  `,
  backButton: css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: var(--cds-spacing-150) var(--cds-spacing-200);
    position: absolute;
    height: 48px;
    top: 16px;
    left: 37px;
    color: var(--cds-color-interactive-primary);
    ${breakpoints.down('xs')} {
      top: 29px;
      right: 0;
    }
  `,
  mobileBackButton: css`
    position: absolute;
    width: 100px;
    left: 6px;
    top: 14px;
    color: var(--cds-color-interactive-primary);
  `,
  mobileExitButton: css`
    position: absolute;
    right: 6px;
    top: 14px;
    padding: 0;
    color: var(--cds-color-interactive-primary);
  `,
  exitButton: css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    padding: 12px 16px;
    position: absolute;
    height: 48px;
    right: 3%;
    top: 16px;
    color: var(--cds-color-interactive-primary);

    ${breakpoints.down('xs')} {
      top: 29px;
      right: 0;
    }
  `,
  greyBar: css`
    width: 100%;
    height: 10px;
    background: var(--cds-color-neutral-disabled);
    border-radius: 10px;
  `,
  fullProgressBar: css`
    width: 100%;
    height: 10px;
    background: var(--cds-color-interactive-primary);
    border-radius: 10px;
  `,
  progressBanner: css`
    margin: var(--cds-spacing-800) 0 var(--cds-spacing-400);
    justify-content: center;
    ${breakpoints.down('sm')} {
      min-width: 0;
    }
    ${breakpoints.down('xs')} {
      min-width: 0;
      margin: var(--cds-spacing-200) 0 var(--cds-spacing-200);
    }
  `,
  coachIconContainer: () => css`
    text-align: center;
    margin-bottom: var(--cds-spacing-100);

    svg {
      width: 82px;
      height: 82px;
    }
    ${breakpoints.down('xs')} {
      svg {
        width: 32px;
        height: 32px;
      }
    }
  `,
};

export const OnboardingHeader = ({
  sectionFlow,
  isMobile,
  switchPage,
  isLoadingPage = false,
  isCourseraPlusUpsellPage = false,
  onExitButtonClick,
  isRedirecting,
}: Props) => {
  const isXsDown = useMediaQuery(breakpoints.down('xs'));
  const currentSection = getCurrentSection(sectionFlow);
  const currentPageIndex = sectionFlow.length;
  const track = useTracker();
  const currentPage = currentPageIndex;
  const currentProgressStep = currentPageIndex;

  const totalProgressSteps = HACK_SURVEY_LENGTH;
  const shouldShowCurrentSectionHeader =
    currentProgressStep > 0 && currentProgressStep < totalProgressSteps + 1 && !isCourseraPlusUpsellPage;

  const formEventData = {
    name: 'onboarding_consumer_multi_page_2022',
    currentStep: currentPageIndex,
    totalSteps: totalProgressSteps,
  } as const;

  const renderCurrentSectionHeader = () => (
    <Grid container css={styles.progressBanner}>
      <Grid item>
        <Typography2 component="p" variant={isXsDown ? 'subtitleLarge' : 'titleMedium'} align="center">
          {currentProgressStep &&
            totalProgressSteps &&
            _t('Step #{currentProgressStep} of #{totalProgressSteps}', { currentProgressStep, totalProgressSteps })}
        </Typography2>
      </Grid>
    </Grid>
  );

  const handleSaveAndExitButton = () => {
    track('click_button', {
      form: formEventData,
      button: { name: 'onboarding_cancel' },
    });
    if (onExitButtonClick) {
      onExitButtonClick();
    } else {
      setLocation('/');
    }
  };
  const handleBackButton = () => {
    track('click_button', {
      form: formEventData,
      button: { name: 'onboarding_back_button' },
    });
    switchPage();
  };
  const shouldHideBackButton = currentPage === 0 || isLoadingPage;

  if (!isMobile) {
    return (
      <Grid>
        <Grid container css={styles.headerContainer}>
          {shouldHideBackButton ? (
            ''
          ) : (
            <Grid item css={styles.backButton}>
              <TrackedButton
                {...Button.defaultProps}
                trackingName="onboarding_back_button"
                trackingData={{ currentPageIndex }}
                variant="ghost"
                data-testid="onboarding-back-button"
                icon={<ChevronPreviousIcon />}
                iconPosition="before"
                onClick={() => handleBackButton()}
              >
                {_t('Back')}
              </TrackedButton>
            </Grid>
          )}
          <Grid item css={styles.courseraLogo}>
            <CourseraLogo />
          </Grid>
          <Grid item css={styles.exitButton}>
            {!isLoadingPage && (
              <TrackedButton
                {...Button.defaultProps}
                trackingName="onboarding_exit_button"
                trackingData={{ currentPageIndex }}
                variant="ghost"
                data-testid="onboarding-exit-button"
                onClick={handleSaveAndExitButton}
                disabled={isRedirecting}
              >
                {_t('Exit')}
              </TrackedButton>
            )}
          </Grid>
        </Grid>
        {shouldShowCurrentSectionHeader && currentSection && renderCurrentSectionHeader()}
      </Grid>
    );
  } else {
    return (
      <Grid>
        <Grid container css={styles.headerContainer}>
          {shouldHideBackButton ? (
            ''
          ) : (
            <Grid item css={styles.mobileBackButton}>
              <TrackedButton
                {...Button.defaultProps}
                trackingName="onboarding_back_button"
                trackingData={{ currentPageIndex }}
                variant="ghost"
                data-testid="onboarding-back-button"
                onClick={() => handleBackButton()}
              >
                <ChevronPreviousIcon />
              </TrackedButton>
            </Grid>
          )}
          <Grid item css={styles.courseraLogo}>
            <CourseraLogo />
          </Grid>
          <Grid item css={styles.mobileExitButton}>
            <TrackedButton
              {...Button.defaultProps}
              trackingName="onboarding_exit_button"
              trackingData={{ currentPageIndex }}
              variant="ghost"
              data-testid="onboarding-exit-button"
              onClick={handleSaveAndExitButton}
              disabled={isRedirecting}
            >
              <CloseIcon />
            </TrackedButton>
          </Grid>
        </Grid>
        {shouldShowCurrentSectionHeader && currentSection && renderCurrentSectionHeader()}
      </Grid>
    );
  }
};
